































import Vue, { CreateElement, PropType, VNode } from "vue";
import { GridCellProps, GridColumnProps, GridNoRecords, GridSortChangeEvent, GridSortSettings } from "@progress/kendo-vue-grid";
import { SortDescriptor } from "@progress/kendo-data-query";
import ApiSearch, { ApiSearchColumn, ColumnType, FilterValue } from "../models/ApiSearch";
import ApiSearchFilterCell from "./ApiSearchFilterCell.vue";
import { SortOrder } from "@/services/filtering";
import { getFieldClasses, getHeaderClasses } from "@/utils/classes";
import { dateFormatHU, dateTimeFormatHU } from "@/filters/date-format";
import moneyFormat from "@/filters/money-format";
import { GridLayoutSettings, columnKey } from "@/services/GridLayoutSettings";
import { GridColumnResizeEvent } from "@progress/kendo-vue-grid/dist/es/interfaces/events";
import GridLayoutMenu from "@/components/GridLayoutMenu.vue";
import eventBus from "@/services/eventBus";
import { VIcon } from "vuetify/lib";
import { t } from "@/plugins/i18n";

type PageSlots = Vue["$scopedSlots"];
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Item = any;
type HeaderCellProps = { field?: string; title?: string; sortable?: boolean };
type HeaderCellListeners = { click?: (event: Event) => void };

interface Data {
  layout: GridLayoutSettings;
}

interface Methods {
  toGridColumn(column: ApiSearchColumn, field: string, index: number): GridColumnProps;
  renderCell(h: CreateElement, props: GridCellProps, column: ApiSearchColumn): VNode;
  renderHeaderCell(h: CreateElement, props: HeaderCellProps, listeners: HeaderCellListeners): VNode;
  formatValue(item: Item, column: ApiSearchColumn): string;
  renderFilterCell(h: CreateElement, column: ApiSearchColumn): VNode;
  renderDetail(h: CreateElement, defaultRendering: VNode | null, props: { dataItem: Item }): VNode;
  renderRow(h: CreateElement, row: VNode, defaultSlots: unknown, props: { dataItem: Item }): VNode;
  onSortChange(event: GridSortChangeEvent): void;
  applySort(sort: SortDescriptor[]): void;
  applyColumnFilters(saved: unknown, initial: boolean): void;
  goToFirstPage(): void;
  onColumnResize(event: GridColumnResizeEvent): void;
  measureColumnWidths(): Record<string, number>;
  openLayoutMenu(event: MouseEvent): void;
  fixDetailColSpan(): void;
}

interface Computed {
  visibleColumns: ApiSearchColumn[];
  baseColumns: GridColumnProps[];
  gridColumns: GridColumnProps[];
  dataItems: Item[];
  expandedCount: number;
  kendoSort: SortDescriptor[];
  sortSettings: GridSortSettings;
  hasColumnWidths: boolean;
}

interface Props {
  items: Item[];
  columns: ApiSearchColumn[];
  apiSearch: ApiSearch;
  /** Az ApiSearchPage-et használó oldal slotjai: a cella (`column.cell`), a szűrő (`column.header`) és a `details` slot. */
  pageSlots: PageSlots;
  loading: boolean;
  overlayProgressIcon: boolean;
  filterable: boolean;
  sortable: boolean;
  hasDetails: boolean;
  detailsField: string;
  openDetails: boolean;
  detailsCloseButton: boolean;
  selectedRowColor: string;
  selectedField: string;
  displayTitles: boolean;
  showItem: (item: Item) => boolean;
  closeItemDetails: (item: Item, event?: Event) => void;
  multiSelectSelectedOptions: Record<string, unknown[]>;
  color: string;
  colorCss: string;
  /** Az elrendezés-profilok kulcsa (rmgenuser_setting.control_name); üresen nincs profil-menü. */
  layoutKey: string;
}

/**
 * Az ApiSearchPage táblája Kendo griddel (`kendoGrid` prop). A szűrés, rendezés, lapozás
 * logikája az `apiSearch` objektumban marad, a grid csak arra köt; a cellák és a részletsor
 * a szülő oldal slotjait kapják, ezért az oldalak slot-kódja változatlan.
 */
export default Vue.extend<Data, Methods, Computed, Props>({
  components: { GridLayoutMenu, "grid-norecords": GridNoRecords },

  props: {
    items: { type: Array as PropType<Item[]>, default: () => [] },
    columns: { type: Array as PropType<ApiSearchColumn[]>, default: () => [] },
    apiSearch: { type: Object as PropType<ApiSearch>, required: true },
    pageSlots: { type: Object as PropType<PageSlots>, default: () => ({}) },
    loading: { type: Boolean, default: false },
    overlayProgressIcon: { type: Boolean, default: false },
    filterable: { type: Boolean, default: true },
    sortable: { type: Boolean, default: true },
    hasDetails: { type: Boolean, default: false },
    detailsField: { type: String, default: "details" },
    openDetails: { type: Boolean, default: false },
    detailsCloseButton: { type: Boolean, default: false },
    selectedRowColor: { type: String, default: "" },
    selectedField: { type: String, default: "selected" },
    displayTitles: { type: Boolean, default: false },
    showItem: { type: Function as PropType<(item: Item) => boolean>, default: () => true },
    closeItemDetails: { type: Function as PropType<(item: Item, event?: Event) => void>, default: () => undefined },
    multiSelectSelectedOptions: { type: Object as PropType<Record<string, unknown[]>>, default: () => ({}) },
    color: { type: String, default: "primary" },
    colorCss: { type: String, default: "var(--primaryColor)" },
    layoutKey: { type: String, default: "" },
  },

  data(): Data {
    // A data() futásakor a computed/methods típusa még nem látszik a this-en; a callbackek később futnak.
    const grid = this as unknown as Vue & Props & Methods & Computed;
    return {
      layout: new GridLayoutSettings({
        controlName: this.layoutKey,
        getBaseColumns: () => grid.baseColumns,
        getSort: () => grid.kendoSort,
        setSort: (sort) => grid.applySort(sort),
        getFilter: () => grid.apiSearch.changedFilterValues(),
        setFilter: (filter, initial) => grid.applyColumnFilters(filter, initial),
        onSortApplied: () => grid.goToFirstPage(),
      }),
    };
  },

  beforeMount() {
    if (this.layoutKey) this.layout.load();
  },

  mounted() {
    this.fixDetailColSpan();
  },

  updated() {
    this.fixDetailColSpan();
  },

  watch: {
    /** `openDetails`: minden sor részletsora nyitva, ugyanúgy, mint a v-simple-table-nél. */
    items: {
      immediate: true,
      handler(items: Item[]) {
        if (!this.hasDetails || !this.openDetails) return;
        items.forEach((item) => {
          if (!item[this.detailsField]) item[this.detailsField] = true;
        });
      },
    },
  },

  computed: {
    visibleColumns(): ApiSearchColumn[] {
      return this.columns.filter((c) => !c.extraFilterValue && !c.isSecod && c.visible);
    },

    /** Az ApiSearchColumn-ok Kendo oszlopként; a `field` nélküli (pl. gomb) oszlop a cella slot nevéből kap kulcsot. */
    baseColumns(): GridColumnProps[] {
      // Ugyanarra a field-re több oszlop is mutathat (pl. kijelölő + azonosító); a saját cellás kap egyedi kulcsot,
      // különben az elrendezés-profil (sorrend, elrejtés) mindkettőt együtt kezelné.
      const counts = new Map<string, number>();
      this.visibleColumns.forEach((c) => {
        if (c.field) counts.set(c.field, (counts.get(c.field) ?? 0) + 1);
      });
      return this.visibleColumns.map((c, index) => {
        const duplicated = !!c.field && (counts.get(c.field) ?? 0) > 1;
        const field = !c.field || (duplicated && c.cell) ? `__cell_${c.cell ?? index}` : c.field;
        return this.toGridColumn(c, field, index);
      });
    },

    gridColumns(): GridColumnProps[] {
      return this.layout.columns;
    },

    dataItems(): Item[] {
      if (this.loading && !this.overlayProgressIcon) return [];
      return this.items.filter((item) => this.showItem(item));
    },

    /** A nyitott részletsorok száma; a template hivatkozik rá, hogy nyitás/zárás után az `updated` lefusson. */
    expandedCount(): number {
      if (!this.hasDetails) return 0;
      return this.dataItems.filter((item) => item[this.detailsField]).length;
    },

    kendoSort(): SortDescriptor[] {
      return (this.apiSearch.sort?.definitions ?? [])
        .filter((d) => d.order !== SortOrder.None)
        .map((d) => ({ field: String(d.field), dir: d.order === SortOrder.Ascending ? "asc" : "desc" }));
    },

    /** Egy oszlop, None → Asc → Desc → None: a SortingModel.toggle ciklusa. */
    sortSettings(): GridSortSettings {
      return this.sortable ? { mode: "single", allowUnsort: true } : false;
    },

    hasColumnWidths(): boolean {
      return Object.keys(this.layout.columnWidths).length > 0;
    },
  },

  methods: {
    toGridColumn(c, field, index) {
      return {
        field,
        // A Kendo üres címnél a field nevét írná a fejlécbe; a régi tábla üresen hagyta.
        title: c.title || " ",
        width: c.width,
        sortable: this.sortable && !!c.sortable && !!c.field && !!c.title,
        filterable: this.filterable && (!!c.filterable || !!c.checkBox),
        headerClassName: getHeaderClasses(c),
        className: getFieldClasses(c),
        cell: (h: CreateElement, _: VNode | null, props: GridCellProps) => this.renderCell(h, props, c),
        headerCell: (h: CreateElement, _: VNode | null, props: HeaderCellProps, listeners: HeaderCellListeners) =>
          this.renderHeaderCell(h, props, listeners),
        filterCell: (h: CreateElement) => this.renderFilterCell(h, c),
        orderIndex: index,
      };
    },

    renderCell(h, props, column) {
      const item = props.dataItem;
      const slot = column.cell ? this.pageSlots[column.cell] : undefined;
      const slotNodes: VNode[] = slot ? slot({ item, column, color: this.colorCss }) ?? [] : [];
      const children: Array<VNode | string> = slot ? slotNodes : [this.formatValue(item, column)];

      const classes = [props.className ?? ""];
      if (column.income) classes.push(item[column.income] ? "income" : "expense");

      // Több oldal slotja maga ad <td>-t: a régi tábla is a saját <td>-jébe ágyazta, és az oldalak
      // igazító szabályai (pl. `.align__field__center td`) erre a belső <td>-re épülnek.
      return h(
        "td",
        {
          class: classes,
          style: props.style,
          attrs: { title: this.displayTitles && column.field ? item[column.field] : undefined },
        },
        children
      );
    },

    /** A régi tábla fejléce: minden cím színes link (a nem rendezhető is), a rendezés iránya fa-sort-alpha ikonnal. */
    renderHeaderCell(h, props, listeners) {
      const dir = this.kendoSort.find((s) => s.field === props.field)?.dir;
      const children: Array<VNode | string> = [props.title ?? ""];
      if (dir) {
        children.push(h("span", { class: `k-icon k-i-sort-${dir}-sm` }));
      }
      return h(
        "a",
        {
          style: { color: this.colorCss },
          on: props.sortable && listeners.click ? { click: listeners.click } : {},
        },
        children
      );
    },

    formatValue(item, column) {
      const value = column.field ? item[column.field] : undefined;
      switch (column.type) {
        case ColumnType.Money:
          return moneyFormat.filter(value, "HUF", undefined);
        case ColumnType.Date:
          return dateFormatHU(value);
        case ColumnType.DateTime:
          return dateTimeFormatHU(value);
        default:
          return value === null || value === undefined ? "" : String(value);
      }
    },

    renderFilterCell(h, column) {
      return h(ApiSearchFilterCell, {
        props: {
          column,
          apiSearch: this.apiSearch,
          multiSelectSelectedOptions: this.multiSelectSelectedOptions,
          items: this.items,
          pageSlots: this.pageSlots,
        },
        on: { "multiselect-change": () => this.$emit("multiselect-change") },
      });
    },

    renderDetail(h, _, props) {
      const item = props.dataItem;
      const close = (event?: Event) => this.closeItemDetails(item, event);
      const slot = this.pageSlots.details;
      const children: VNode[] = slot ? [...(slot({ item, color: this.colorCss, close }) ?? [])] : [];

      if (this.detailsCloseButton && !this.openDetails) {
        children.push(
          h("div", { class: "details-close", attrs: { title: t("Bezaras") }, on: { click: close } }, [
            h(VIcon, { props: { small: true, color: this.color } }, "fa-chevron-up"),
          ])
        );
      }

      return h("div", { class: "row-details" }, children);
    },

    renderRow(_h, row, _defaultSlots, props) {
      const item = props.dataItem;
      if (row && this.selectedRowColor && item && item[this.selectedField]) {
        row.data = { ...row.data, style: { ...(row.data?.style as object), backgroundColor: this.selectedRowColor } };
      }
      return row;
    },

    onSortChange(event) {
      this.applySort(event.sort);
    },

    applySort(sort) {
      const model = this.apiSearch.sort;
      if (!model) return;

      const next = sort[0];
      model.definitions.forEach((d) => {
        d.order =
          next && d.field === next.field
            ? next.dir === "desc"
              ? SortOrder.Descending
              : SortOrder.Ascending
            : SortOrder.None;
      });
    },

    /**
     * A profil szűrői (csak az alaptól eltérők vannak benne) az alapszűrőre rakva; ami a profilban nincs,
     * alapállapotba kerül. A szűrő watcher az értékváltozásra magától újratölt. Induláskor a már módosított
     * szűrő (pl. navigáció után visszatöltött állapot) nyer az alapértelmezett profil felett.
     */
    applyColumnFilters(saved, initial) {
      const values = (saved ?? {}) as Record<string, FilterValue>;
      const filters = this.apiSearch.filter?.filters as Record<string, FilterValue> | undefined;
      if (!filters) return;
      if (initial && (Object.keys(this.apiSearch.changedFilterValues()).length > 0 || Object.keys(values).length === 0)) return;

      this.apiSearch.resetFiltering();
      this.apiSearch.applyFilterValues(values);

      // A többválasztós szűrőcella a filteren kívüli map-ből olvas.
      Object.keys(this.multiSelectSelectedOptions).forEach((key) => {
        const f = filters[key];
        if (f && "selectedOptions" in f) this.$set(this.multiSelectSelectedOptions, key, [...(f.selectedOptions ?? [])]);
      });
    },

    /** Profilváltás után a szűrt lista rövidebb lehet az aktuális oldalszámnál. */
    goToFirstPage() {
      if (this.apiSearch.currentPage !== 1) eventBus.$emit("api-pager:set-page", 1);
    },

    onColumnResize(event) {
      // Fix elrendezésben csak akkor marad meg a többi oszlop mérete, ha mindnek van rögzített szélessége.
      if (!this.hasColumnWidths) this.layout.columnWidths = this.measureColumnWidths();
      this.layout.columnResize(event);
    },

    measureColumnWidths() {
      const ths = this.$el.querySelectorAll<HTMLTableCellElement>("thead tr:first-child th");
      const widths: Record<string, number> = {};
      this.gridColumns.forEach((c, i) => {
        const th = ths[i];
        if (th) widths[columnKey(c)] = Math.ceil(th.getBoundingClientRect().width);
      });
      return widths;
    },

    openLayoutMenu(event) {
      if (!this.layoutKey) return;
      (this.$refs.layoutMenu as InstanceType<typeof GridLayoutMenu>).show(event);
    },

    /** A Kendo a részletcella colSpan-ját oszlopszám-1-re veszi, mert hierarchia-oszlopot feltételez; nálunk az nincs. */
    fixDetailColSpan() {
      const count = this.gridColumns.length;
      this.$el.querySelectorAll<HTMLTableCellElement>("td.k-detail-cell").forEach((td) => {
        if (td.colSpan !== count) td.colSpan = count;
      });
    },
  },
});
